import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useParentContext, type ParentChild } from "@/lib/parent-context";
import { supabase } from "@/integrations/supabase/client";
import { Bus, MapPin, Phone, Clock, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/parent/transport")({
  ssr: false,
  beforeLoad: () => requireRole("parent"),
  head: () => ({ meta: [{ title: "Transport — MAKAI" }] }),
  component: TransportPage,
});

interface RouteInfo {
  routeId: string;
  routeName: string;
  driverName: string | null;
  driverPhone: string | null;
  pickup: string | null;
  dropoff: string | null;
  pickupTime: string | null;
  status: string;
  lastPing: { lat: number; lng: number; speed: number | null; at: string } | null;
}

function TransportPage() {
  const { ctx } = useParentContext();
  const [child, setChild] = useState<ParentChild | null>(null);
  const [info, setInfo] = useState<RouteInfo | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (ctx && !child) setChild(ctx.children[0] ?? null); }, [ctx, child]);

  useEffect(() => {
    if (!child) { setInfo(null); return; }
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    const load = async () => {
      setBusy(true);
      const { data: assign } = await supabase
        .from("transport_assignments")
        .select("route_id, pickup_point, transport_routes:route_id(id, name, driver_name, driver_phone)")
        .eq("learner_id", child.learnerId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!assign || cancelled) { setInfo(null); setBusy(false); return; }
      const route = assign.transport_routes as { id: string; name: string; driver_name: string | null; driver_phone: string | null } | null;
      const { data: ping } = await supabase
        .from("transport_pings")
        .select("lat, lng, speed_kph, status, recorded_at")
        .eq("route_id", assign.route_id)
        .order("recorded_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      setInfo({
        routeId: assign.route_id,
        routeName: route?.name ?? "Route",
        driverName: route?.driver_name ?? null,
        driverPhone: route?.driver_phone ?? null,
        pickup: assign.pickup_point,
        dropoff: null,
        pickupTime: null,
        status: ping?.status ?? "active",
        lastPing: ping && ping.lat != null && ping.lng != null
          ? { lat: Number(ping.lat), lng: Number(ping.lng), speed: ping.speed_kph ? Number(ping.speed_kph) : null, at: ping.recorded_at }
          : null,
      });
      setBusy(false);
    };
    load();
    timer = setInterval(load, 20000);
    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, [child]);

  const mapUrl = useMemo(() => {
    if (!info?.lastPing) return null;
    const { lat, lng } = info.lastPing;
    return `https://www.openstreetmap.org/export/embed.html?bbox=${lng - 0.01}%2C${lat - 0.01}%2C${lng + 0.01}%2C${lat + 0.01}&layer=mapnik&marker=${lat}%2C${lng}`;
  }, [info]);

  return (
    <AppShell role="parent" title="Transport tracking" subtitle="Live bus location and driver details for peace of mind.">
      {ctx && ctx.children.length > 1 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {ctx.children.map((c) => (
            <button key={c.learnerId} type="button" onClick={() => setChild(c)}
              className={cn("rounded-full border px-3 py-1.5 text-xs font-semibold",
                child?.learnerId === c.learnerId ? "border-primary bg-primary-soft text-primary" : "border-border bg-card")}>{c.firstName}</button>
          ))}
        </div>
      )}

      {busy && !info && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Locating bus…</div>}

      {!busy && !info && (
        <div className="rounded-2xl border border-dashed border-border bg-muted/30 p-10 text-center text-sm text-muted-foreground">
          {child?.firstName ?? "Your child"} is not assigned to a school transport route.
        </div>
      )}

      {info && (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
          <article className="overflow-hidden rounded-2xl border border-border bg-card shadow-soft">
            {mapUrl ? (
              <iframe title="bus location" src={mapUrl} className="h-80 w-full" loading="lazy" />
            ) : (
              <div className="grid h-80 place-items-center bg-muted text-xs text-muted-foreground">No GPS ping received yet.</div>
            )}
            <div className="grid gap-2 p-4 text-xs">
              <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-primary" /> Pickup: <span className="font-medium text-foreground">{info.pickup ?? "—"}</span></div>
              <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-primary" /> Drop-off: <span className="font-medium text-foreground">{info.dropoff ?? "—"}</span></div>
              {info.pickupTime && <div className="flex items-center gap-2"><Clock className="h-3.5 w-3.5 text-primary" /> Pickup time: <span className="font-medium text-foreground">{info.pickupTime.slice(0,5)}</span></div>}
            </div>
          </article>

          <aside className="space-y-3">
            <div className="rounded-2xl border border-border bg-card p-4 shadow-soft">
              <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Bus className="h-4 w-4" /> Route
              </div>
              <div className="font-display text-base font-bold">{info.routeName}</div>
              {info.lastPing && (
                <div className="mt-1 text-[11px] text-muted-foreground">
                  Last update {new Date(info.lastPing.at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                  {info.lastPing.speed != null && <span> · {Math.round(info.lastPing.speed)} km/h</span>}
                </div>
              )}
            </div>
            <div className="rounded-2xl border border-border bg-card p-4 shadow-soft">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Driver</div>
              <div className="text-sm font-semibold">{info.driverName ?? "Driver"}</div>
              {info.driverPhone && (
                <a href={`tel:${info.driverPhone}`} className="mt-2 flex items-center gap-2 rounded-xl bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground">
                  <Phone className="h-3.5 w-3.5" /> Call {info.driverPhone}
                </a>
              )}
            </div>
          </aside>
        </div>
      )}
    </AppShell>
  );
}
